import React, { useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useLocation as useRouterLocation, Link } from "react-router-dom";
import { useLocation } from "../../context/LocationContext";
import { useAuth } from "../../context/AuthContext";
import AdminContextSelector from "../admin/AdminContextSelector";
import AgentStatusSelector from "../calls/AgentStatusSelector";
import {
  Bars3Icon,
  ChevronDownIcon,
  MapPinIcon,
  BellIcon,
  ChevronRightIcon,
  CheckIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

const getPageInfo = (pathname) => {
  // Admin sub-pages
  if (pathname.startsWith("/admin/")) {
    const subPage = pathname.split("/admin/")[1];
    const subTitles = {
      organizations: "Organizations",
      users: "Admin Users",
    };
    return {
      title: subTitles[subPage] || "Admin",
      breadcrumbs: [
        { name: "Admin", href: "/admin" },
        { name: subTitles[subPage] || subPage },
      ],
    };
  }

  // Settings sub-pages
  if (pathname.startsWith("/settings/")) {
    const subPage = pathname.split("/settings/")[1];
    const subTitles = {
      organization: "Organization Settings",
      locations: "Locations",
      users: "Team Members",
      pms: "PMS Integration",
      notifications: "Notifications",
      security: "Security",
    };
    return {
      title: subTitles[subPage] || "Settings",
      breadcrumbs: [
        { name: "Settings", href: "/settings" },
        { name: subTitles[subPage] || subPage },
      ],
    };
  }

  if (pathname === "/agent/my-templates") {
    return { title: "My Templates", breadcrumbs: null };
  }

  const titles = {
    "/admin": { title: "Admin Dashboard", breadcrumbs: null },
    "/dashboard": { title: "Dashboard", breadcrumbs: null },
    "/appointments": { title: "Appointments", breadcrumbs: null },
    "/consultations": { title: "Consultations", breadcrumbs: null },
    "/follow-ups": { title: "Follow-Ups", breadcrumbs: null },
    "/conversations": { title: "Conversations", breadcrumbs: null },
    "/tasks": { title: "Tasks", breadcrumbs: null },
    "/coaching": { title: "Coaching", breadcrumbs: null },
    "/pipeline": { title: "Pipeline", breadcrumbs: null },
    "/call-history": { title: "Call History", breadcrumbs: null },
    "/analytics": { title: "Analytics", breadcrumbs: null },
    "/reactivation": { title: "Reactivation", breadcrumbs: null },
    "/settings": { title: "Settings", breadcrumbs: null },
    "/smile-preview": { title: "Smile Preview", breadcrumbs: null },
  };
  return titles[pathname] || { title: "Dashboard", breadcrumbs: null };
};

const Header = ({ onMenuClick }) => {
  const routerLocation = useRouterLocation();
  const { locations, currentLocation, switchLocation } = useLocation();
  const { user, logout } = useAuth();
  const { title, breadcrumbs } = getPageInfo(routerLocation.pathname);

  const initials =
    `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase();
  const locationList = Array.isArray(locations) ? locations : [];
  const selectedLocation =
    locationList.find(
      (l) => String(l?._id ?? l) === String(currentLocation?._id ?? ""),
    ) || null;
  const locationLabel =
    user?.role === "super_admin"
      ? "Context"
      : selectedLocation?.name || currentLocation?.name || "Select";

  return (
    <header className="sticky top-0 z-30 border-b border-surface-200/50 bg-white/80 backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3 h-16 px-4 sm:px-6 min-w-0">
        {/* Left section */}
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-xl text-surface-500 hover:text-surface-700 hover:bg-surface-100 transition-all duration-200"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* Breadcrumbs or Title */}
          {breadcrumbs ? (
            <>
              {/* Hide breadcrumbs on mobile/tablet, show on desktop */}
              <nav className="hidden lg:flex items-center gap-2">
                {breadcrumbs.map((crumb, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <ChevronRightIcon className="w-4 h-4 text-surface-300" />
                    )}
                    {crumb.href ? (
                      <Link
                        to={crumb.href}
                        className="text-sm font-medium text-surface-500 hover:text-surface-700 transition-colors"
                      >
                        {crumb.name}
                      </Link>
                    ) : (
                      <span className="text-sm font-semibold text-dark-900">
                        {crumb.name}
                      </span>
                    )}
                  </Fragment>
                ))}
              </nav>
              {/* Show only the final page title on mobile/tablet */}
              <div className="hidden sm:block lg:hidden">
                <h1 className="text-lg font-bold text-dark-900">
                  {breadcrumbs[breadcrumbs.length - 1].name}
                </h1>
              </div>
            </>
          ) : (
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-dark-900">{title}</h1>
            </div>
          )}
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2 min-w-0 shrink-0">
          {/* Agent Status (agent and team_leader) */}
          {(user?.role === "agent" || user?.role === "team_leader") && (
            <AgentStatusSelector compact />
          )}

          {/* Location Switcher */}
          {user?.role !== "super_admin" && locationList.length > 0 && (
            <Menu as="div" className="relative">
              <Menu.Button
                className="flex items-center gap-2 min-w-0 max-w-full px-3 py-2 rounded-xl text-sm font-medium
                text-surface-600 bg-surface-100 hover:bg-surface-200/80 
                border border-surface-200 hover:border-surface-300
                transition-all duration-200"
              >
                <div className="w-6 h-6 shrink-0 rounded-lg bg-brand-500/10 flex items-center justify-center">
                  <MapPinIcon className="w-3.5 h-3.5 text-brand-500" />
                </div>
                <span className="min-w-0 truncate max-w-[9rem] sm:max-w-[14rem] md:max-w-[18rem] lg:max-w-[min(22rem,45vw)]">
                  {locationLabel}
                </span>
                <ChevronDownIcon className="w-4 h-4 shrink-0 text-surface-400" />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 scale-95 translate-y-1"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 translate-y-1"
              >
                <Menu.Items className="absolute right-0 mt-2 w-[min(28rem,calc(100vw-2rem))] min-w-[15rem] max-h-72 overflow-auto bg-white rounded-2xl shadow-2xl border border-surface-200 focus:outline-none">
                  {/* Header */}
                  <div className="px-4 py-3 bg-gradient-to-r from-brand-500/5 to-transparent border-b border-surface-100">
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4 text-brand-500" />
                      <p className="text-sm font-semibold text-dark-900">
                        Switch Location
                      </p>
                    </div>
                  </div>

                  {/* Locations list */}
                  <div className="py-2 w-max min-w-full">
                    {locationList.map((location) => (
                      <Menu.Item key={location._id}>
                        {({ active }) => (
                          <button
                            onClick={() => switchLocation(location._id)}
                            className={clsx(
                              "flex w-max min-w-full items-center gap-3 px-4 py-3 text-sm transition-all duration-200",
                              active ? "bg-surface-50" : "",
                            )}
                          >
                            <div
                              className={clsx(
                                "w-10 h-10 shrink-0 rounded-xl flex items-center justify-center transition-all",
                                selectedLocation?._id === location._id
                                  ? "bg-brand-500 text-white"
                                  : "bg-surface-100 text-surface-500",
                              )}
                            >
                              <MapPinIcon className="w-5 h-5 shrink-0" />
                            </div>
                            <div className="text-left">
                              <p
                                className={clsx(
                                  "font-medium whitespace-nowrap leading-snug",
                                  selectedLocation?._id === location._id
                                    ? "text-brand-600"
                                    : "text-dark-900",
                                )}
                              >
                                {location.name}
                              </p>
                              {location.address?.city && (
                                <p className="text-xs text-surface-500 whitespace-nowrap">
                                  {location.address.city},{" "}
                                  {location.address.state}
                                </p>
                              )}
                            </div>
                            {selectedLocation?._id === location._id && (
                              <div className="w-6 h-6 shrink-0 rounded-full bg-brand-500/10 flex items-center justify-center">
                                <CheckIcon className="w-4 h-4 shrink-0 text-brand-500" />
                              </div>
                            )}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          )}

          {/* Notifications */}
          <Menu as="div" className="relative">
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-1"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-1"
            >
              <Menu.Items className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-surface-200 overflow-hidden focus:outline-none">
                <div className="px-4 py-3 border-b border-surface-100 flex items-center justify-between">
                  <p className="text-sm font-semibold text-dark-900">
                    Notifications
                  </p>
                  <span className="px-2 py-0.5 text-xs font-medium text-brand-600 bg-brand-50 rounded-full">
                    3 new
                  </span>
                </div>
                <div className="py-2 divide-y divide-surface-100">
                  <div className="px-4 py-3 hover:bg-surface-50 transition-colors cursor-pointer">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-4 h-4 text-brand-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-dark-900">
                          SOAP note generated for Sarah J.
                        </p>
                        <p className="text-xs text-surface-500 mt-0.5">
                          2 minutes ago
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 hover:bg-surface-50 transition-colors cursor-pointer">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-success-100 flex items-center justify-center flex-shrink-0">
                        <CheckIcon className="w-4 h-4 text-success-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-dark-900">
                          Follow-up sent successfully
                        </p>
                        <p className="text-xs text-surface-500 mt-0.5">
                          15 minutes ago
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 border-t border-surface-100 bg-surface-50">
                  <Link
                    to="/notifications"
                    className="text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    View all notifications
                  </Link>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>

          {/* User Menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2 p-1.5 pr-3 rounded-xl hover:bg-surface-100 transition-all duration-200">
              {user?.avatar ? (
                <img
                  src={user?.avatar}
                  alt="user avatar"
                  className="w-8 h-8 rounded-xl object-contain"
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white text-sm font-semibold">
                  {initials}
                </div>
              )}
              <ChevronDownIcon className="w-4 h-4 text-surface-400 hidden sm:block" />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-1"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-1"
            >
              <Menu.Items className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-surface-200 overflow-hidden focus:outline-none">
                {/* User info */}
                <div className="px-4 py-4 bg-gradient-to-r from-brand-500/5 to-transparent border-b border-surface-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white font-semibold">
                      {user?.avatar ? (
                        <img
                          src={user?.avatar}
                          alt="user avatar"
                          className="w-12 h-12 rounded-xl object-contain"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white text-sm font-semibold">
                          {initials}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-dark-900 truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-sm text-surface-500 truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu items */}
                <div className="py-2">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/settings/profile"
                        className={clsx(
                          "flex items-center gap-3 px-4 py-2.5 text-sm",
                          active ? "bg-surface-50" : "",
                          "text-surface-700",
                        )}
                      >
                        <UserCircleIcon className="w-5 h-5 text-surface-400" />
                        <span>Your Profile</span>
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/settings"
                        className={clsx(
                          "flex items-center gap-3 px-4 py-2.5 text-sm",
                          active ? "bg-surface-50" : "",
                          "text-surface-700",
                        )}
                      >
                        <Cog6ToothIcon className="w-5 h-5 text-surface-400" />
                        <span>Settings</span>
                      </Link>
                    )}
                  </Menu.Item>
                </div>

                <div className="border-t border-surface-100 py-2">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={logout}
                        className={clsx(
                          "w-full flex items-center gap-3 px-4 py-2.5 text-sm",
                          active ? "bg-error-50" : "",
                          "text-error-600",
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5" />
                        <span>Sign out</span>
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default Header;
